import { useQuery } from "@tanstack/react-query";

import { CACHED_TIME } from "@/modules/irohana-components/constants/common";
import { QUERY_KEYS } from "@/modules/irohana-components/constants/queryKeys";
import { valueOf } from "@/modules/irohana-components/types/common";
import { createUrlWithParams } from "@/modules/irohana-components/utils/url";
import { globalApi } from "@/modules/irohana-components/utils/api";
import { TGetEnumOptionReqDto, TGetEnumOptionsResDto } from "@/modules/irohana-components/api/services/global/enum/dtos/get-enum-options.dto";

export type TEnumName = valueOf<
  [
    "EAdminUserRole",
    "EAdminUserStatus",
    "EInformationType",
    "EEmploymentStatus",
    "ERetirementStatus",
    "EDocumentStatus",
    "EQuarter",
    "EGender",
    "EEmployeeTrainingStatus",
    "EEmployeeCareerCompletionProof",
    "EEmployeeCareerSkillCertificationOne",
    "EEmployeeCareerLocation",
    "EEmployeeCareerJapaneseCertification",
    "ECompanySupporterRole",
    "ECompanyCondition",
    "EImmigrationJurisdiction",
    "EPayrollDeadline",
    "EMaritalStatus",
    "EResidenceCardReceiptMethod",
    "EYesNoAnswer",
    "EYesNoAndNotApplicableAnswer",
    "EPaymentMethod",
    "ERenewVisaConditionsApplyForStaffingTarget",
    "ERenewVisaConditionsApplyForSupportPlanDelegation",
    "EPaymentDate",
    "ECompanyDivision",
    "ERsoDivision",
    "ERsoIndividualSubDivision",
    "ERsoCorporateSubDivision",
    "ERenewVisaDocumentStatus",
    "ERSOSupporterRole",
    "ECompanyContractStatus",
    "ERenewVisaApplicationResult",
    "EEmailDelivery",
    "ESupplierType",
    "ECoeDocumentStatus",
    "ECoeApplicationType",
    "ECoeHumanResourcesRoute",
    "EPurposeOfEntry",
    "ECoeApplicationResult",
    "ECoeConditionsApplyForStaffingTarget",
    "EUploadFileItem",
    "ECoeResidenceCardReceiptMethod",
    "ERequestOnlineUploadFileItemStatus",
    "EFileItemHistoryUploadFromType",
    "EContractRenewalStatus",
    "EContractConditionRenewal",
    "EEmploymentType",
    "EWorkingMode",
    "ENonRegularDay",
    "EInsuranceType",
    "ELanguageCode",
    "EWageType",
    "EConditionDocumentStatus",
    "EWorkingFlexibleType",
    "ESupportPlanDocumentStatus",
    "EGuidanceImplementMethod",
    "EAirportArrivalImplementMethod",
    "EAirportDepartureImplementMethod",
    "EHousingProvisionStatus",
    "EHousingProvisionFacilityType",
    "ELifeSupportImplementMethod",
    "EOrientationInfoImplementMethod",
    "EConsultMethod",
    "EConsultMeetingImplementMethod",
    "ECoexistenceMethod",
    "ECoexistenceResidence",
    "EWhetherRelatedActivitySpecificSkillTransitionPeriod",
    "EWhetherRsoSupportFee",
    "EWhetherEmploymentAgency",
    "EConvertError",
    "EModifyContractModificationReason",
    "EModifyContractChangeDispatchLocationReasons",
    "EModifyContractTerminateReasonForCompany",
    "EModifyContractTerminateReasonForEmployee",
    "EModifyContractTerminateReason",
    "EEmploymentContractModificationType",
    "ETerminateContractMajorCategoryReason",
    "ETerminateContractSubcategoryReason",
    "ESupportContractSubcategoryReason",
    "ESupportRegistered",
    "EAnyTimeContentOfSupport",
    "ESupportOutsourcingContractModificationType",
    "ESupportRecipient",
    "ESupportSpecifiedSkill",
    "ESpecificCircumstance",
    "ESpecificCircumstanceContent",
    "EAnyConsultation",
    "EAnyConsultationContent",
    "EPlanToChangeJob",
    "EPlanToChangeJobContent",
    "ESupportType",
    "ESupportContent",
    "EReasonReturnHome",
    "EReasonReturnHomeContent",
    "EPlanDateReturn",
    "EPlanDateReturnContent",
    "EPlanToMove",
    "EPlanToMoveContent",
    "ECurrentSituation",
    "EProvidedLegalProtection",
    "EPlanToContinueActivity",
    "EMeasure",
    "ERetirementReasonType",
    "EEmployeeRetirementDocumentStepStatus",
    "EFormOfContract",
    "EEntrustAnswer",
    "EArrivalAirport",
    "ERegularDocumentStatus",
    "ERegularAppendixBranchEmployeeSupportType",
    "ERegularDocumentAppendixPaymentMethod",
    "ERenewVisaCondition",
    "ERecruitmentStatus",
    "EMatchContractStatus",
    "EJobStatus",
    "EJobTag",
    "EClientType",
    "ESalaryType",
    "EYesNoPendingStatus",
    "EJapaneseLevelType",
    "ERecruitmentLocationType",
    "EForeignLanguage",
    "EEducationLevel",
    "EPartnerContactMethod",
    "EJobCaseFilter",
    "ECaseStatus",
    "EJobResidenceStatus",
    "ETalentContactType",
    "EInterviewMethod",
    "EMeetingToolType",
    "EUserType",
    "EAdminRole",
    "EUserActiveStatus",
    "EDocumentType",
    "EDocumentKey",
    "EDocumentStepKey",
    "ESubmitStatus",
    "EEmployeeResidencePeriod",
    "EEmployeeResidenceStatus",
    "EEmployeeContractMainOccupation",
    "ESupportImplementationStatus",
    "EProvince",
    "EEmployeeCareerSkillExamOne",
    "EEmployeeCareerJapaneseExam",
    "EConvertStatus",
    "EContactType",
    "ECompanySpecificIndustry",
    "EMaintenanceMode",
    "EConvertDocumentName",
    "ERSOContractStatus",
    "EPartType",
    "ESpecifiedSkillPassed",
    "EPlanStatus",
    "ECurriculumProgress",
  ]
>;

export const fetchEnumApi = async (enumName: TEnumName, req?: TGetEnumOptionReqDto): Promise<TGetEnumOptionsResDto> => {
  const url = createUrlWithParams(`/enums/${enumName}/options`, req);
  const rs = await globalApi.get<TGetEnumOptionsResDto>(url);
  return rs.data;
};

export const useEnum = (enumName?: TEnumName, req?: TGetEnumOptionReqDto) => {
  const { data, ...rest } = useQuery({
    queryKey: [QUERY_KEYS.GLOBAL.FETCH_ENUM, enumName, req?.lang],
    queryFn: () => (enumName ? fetchEnumApi(enumName, req) : undefined),
    enabled: Boolean(enumName),
    staleTime: CACHED_TIME,
    gcTime: CACHED_TIME,
  });

  return { data: data?.data ?? [], ...rest };
};
